import { auth } from "./setup";
import { dealerStorage, getDealerNameFromUrl } from "src/utils";
import type { ActionCodeSettings } from "firebase/auth";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import type {
  CreateMagicLinkFunction,
  CreateUserFunction,
  LoginUserFunction,
  ProcessMagicLinkFunction,
  SendSMSCodeFunction,
  SignInWithSMSCodeFunction,
} from "src/types/auth";

/******************
 * Email/Password *
 ******************/
export const createUser: CreateUserFunction = (email, password) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

export const loginUser: LoginUserFunction = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

/**************
 * Magic Link *
 **************/
export const createMagicLink: CreateMagicLinkFunction = (email, dealerName) => {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectParam = urlParams.get("redirect");
  const path = redirectParam
    ? `${dealerName}/dashboard?redirect=${decodeURIComponent(redirectParam)}&email=${encodeURIComponent(email)}`
    : `${dealerName}/dashboard?email=${encodeURIComponent(email)}`;
  const url = new URL(path, __FRONTEND_BASE_URL__).toString();
  const config: ActionCodeSettings = {
    url,
    handleCodeInApp: true,
  };
  return sendSignInLinkToEmail(auth, email, config);
};

export const processMagicLink: ProcessMagicLinkFunction = (navigate) => {
  const dealerName = getDealerNameFromUrl();
  let url = window.location.href;

  // Check if we received a magic link URL from a cross-domain redirect
  // (e.g., from dealer-portal-web-client's auth action handler)
  const urlParams = new URLSearchParams(window.location.search);
  const magicLinkParam = urlParams.get("magicLink");
  if (magicLinkParam) {
    url = magicLinkParam;
  }

  if (isSignInWithEmailLink(auth, url)) {
    // Try to get email from multiple sources:
    // 1. URL parameter (from cross-domain redirect)
    // 2. localStorage (same-device flow)
    // 3. Prompt user (fallback)
    let email = urlParams.get("email");

    if (!email) {
      email = dealerStorage.getItem("email-for-sign-in");
    }

    if (!email) {
      // User opened the link on a different device. To prevent session fixation
      // attacks, ask the user to provide the associated email again. For example:
      const userInput = window.prompt(
        "Please provide your email for confirmation",
      );

      // Trim whitespace and explicitly check for null (cancel) or empty input
      email = userInput ? userInput.trim() : null;

      if (!email) {
        console.warn(
          "Magic link email confirmation was required but not provided.",
        );
        return; // Exit early if the user cancels or submits an empty email
      }
    }
    // The client SDK will parse the code from the link for you.
    signInWithEmailLink(auth, email, url)
      .then((_) => {
        console.log("MAGIC LINK CONFIRMED");
        dealerStorage.removeItem("email-for-sign-in");
        dealerStorage.setItem("email-for-register", email);

        // Clean up URL by removing magic link params
        if (magicLinkParam) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete("magicLink");
          cleanUrl.searchParams.delete("email");
          window.history.replaceState({}, "", cleanUrl.toString());
        }
      })
      .catch((error: { code?: string }) => {
        console.log("MAGIC LINK CONFIRMATION ERROR:", error);
        if (error?.code === "auth/invalid-action-code") {
          console.log("Expired magic link");
          navigate(`/${dealerName}/login?error=expired-magiclink`);
        } else {
          navigate(`/${dealerName}/login?error=other`);
        }
      });
  }
};

/**************
 * Phone Code *
 **************/
export const sendSMSCode: SendSMSCodeFunction = (
  phoneNumber,
  onSuccess,
  onError,
) => {
  setUpRecaptcha();
  signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier)
    .then((confirmationResult) => {
      window.confirmationResult = confirmationResult;
      localStorage.setItem("verificationId", confirmationResult.verificationId);
      if (onSuccess) {
        onSuccess(confirmationResult);
      }
    })
    .catch((error) => {
      console.log("SEND SMS ERROR:", error);
      if (onError) {
        onError();
      }
    });
};

export const signInWithSMSCode: SignInWithSMSCodeFunction = (
  smsCode,
  onSuccess,
  onError,
) => {
  const verificationId =
    window.confirmationResult?.verificationId ||
    localStorage.getItem("verificationId");
  if (!verificationId) return;
  const credential = PhoneAuthProvider.credential(verificationId, smsCode);

  signInWithCredential(auth, credential)
    .then((result) => {
      console.log("SMS sign in success", result);
      localStorage.removeItem("verificationId");
      dealerStorage.removeItem("phone-for-sign-in");
      if (result.user.phoneNumber) {
        dealerStorage.setItem("phone-for-register", result.user.phoneNumber);
      }

      if (onSuccess) {
        onSuccess();
      }
    })
    .catch((error) => {
      console.log("CONFIRM SMS ERROR:", error);
      if (onError) {
        onError();
      }
    });
};

/**
 * {@link https://github.com/firebase/firebase-js-sdk/issues/8844|GitHub Issue #8844}
 * The Recaptcha callbacks use a private API workaround for recaptcha reset based on community discussion.
 */
function forceResetRecaptcha() {
  /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error TS2341 */
  window.recaptchaVerifier?.recaptcha?.reset();
  /* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/ban-ts-comment */
}

// Required Pre-requisite for sms codes
export const setUpRecaptcha = () => {
  if (!window.recaptchaVerifier) {
    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      {
        size: "invisible",
        callback: () => {
          forceResetRecaptcha();
        },
        "expired-callback": () => {
          forceResetRecaptcha();
        },
        "error-callback": () => {
          forceResetRecaptcha();
        },
      },
    );
  }

  void window.recaptchaVerifier.render().then((widgetId) => {
    window.recaptchaWidgetId = widgetId;
  });
};
